import React from 'react';
import { Button, Card, Flex, FlexColumn, Tooltip } from 'core/components';
import { FieldLabel } from 'core/form/components/FieldLabel';
import { TextArea } from 'core/form';
import Markdown from 'app/components/markdown/Markdown';

const MarkdownPreviewTextArea = (props) => {
  const { field, rows = 10, fillHeight = false, inputRef: fieldInputRef, ...rest } = props;
  const [isMarkdown, setIsMarkdown] = React.useState(false);
  const [cardHeight, setCardHeight] = React.useState(null);
  const observerRef = React.useRef(null);

  const inputRef = React.useCallback(
    (el) => {
      if (fieldInputRef) {
        if (typeof fieldInputRef === 'function') {
          fieldInputRef(el);
        } else if (fieldInputRef && 'current' in fieldInputRef) {
          fieldInputRef.current = el;
        }
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (el) {
        setCardHeight(el.offsetHeight);
        const observer = new ResizeObserver(() => setCardHeight(el.offsetHeight));
        observer.observe(el);
        observerRef.current = observer;
      }
    },
    [fieldInputRef]
  );

  return (
    <FlexColumn {...(fillHeight ? { flex: 1, minHeight: 0 } : { mb: 1 })}>
      <Flex gap="4px" alignItems="center" justifyContent="flex-start" mb="4px">
        <FieldLabel fontWeight="bold">{field.label}</FieldLabel>
        <Tooltip content="Toggle Markdown view">
          <Button
            small
            minimal
            intent="primary"
            icon={isMarkdown ? 'eye-off' : 'eye-open'}
            active={isMarkdown}
            onClick={() => setIsMarkdown(!isMarkdown)}
          />
        </Tooltip>
      </Flex>

      {!isMarkdown && (
        <TextArea
          {...(!fillHeight && { rows })}
          fill
          field={field}
          inputRef={inputRef}
          style={fillHeight ? { flex: 1, minHeight: 0 } : undefined}
          {...rest}
        />
      )}
      {isMarkdown && (
        <Card
          p={1}
          overflow="auto"
          // Height is kept in sync with the real TextArea via ResizeObserver.
          // Fall back to a rough calculation only before the first measurement.
          height={cardHeight ?? 14 * 1.294 * rows + 16}
          border="thin"
          boxShadow="none"
        >
          <Markdown>{field.getValue()}</Markdown>
        </Card>
      )}
    </FlexColumn>
  );
};

export default MarkdownPreviewTextArea;
